import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Play, ExternalLink } from 'lucide-react';
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface YouTubeLinkProps {
  youtubeId: string;
  title: string;
  description?: string;
  className?: string;
  aspectRatio?: string;
  thumbnailQuality?: 'default' | 'hqdefault' | 'mqdefault' | 'sddefault' | 'maxresdefault';
  showThumbnail?: boolean;
  /** Optional local placeholder image path (DSGVO-compliant, no Google request) */
  placeholderImage?: string;
}

const YouTubeLink: React.FC<YouTubeLinkProps> = ({
  youtubeId,
  title,
  description,
  className = "",
  aspectRatio = "16/9",
  thumbnailQuality = "hqdefault",
  showThumbnail = true,
  placeholderImage
}) => {
  const [consentGiven, setConsentGiven] = useState(false);

  const ratioValues = aspectRatio.split('/');
  const ratio = parseInt(ratioValues[0]) / parseInt(ratioValues[1]);

  const youtubeUrl = `https://youtu.be/${youtubeId}`;

  // DSGVO: Only load thumbnail from Google after explicit user consent
  const thumbnailUrl = consentGiven
    ? `https://img.youtube.com/vi/${youtubeId}/${thumbnailQuality}.jpg`
    : null;

  return (
    <div className={className}>
      {showThumbnail ? (
        <div className="relative rounded-lg overflow-hidden shadow-md bg-joerg-100 group cursor-pointer">
          <AspectRatio ratio={ratio}>
            {/* Background: local placeholder or consent-based thumbnail */}
            {thumbnailUrl ? (
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                style={{ backgroundImage: `url(${thumbnailUrl})` }}
              />
            ) : placeholderImage ? (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${placeholderImage})` }}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-joerg-100 to-joerg-200 flex items-center justify-center">
                <Play className="w-12 h-12 text-joerg-400" />
              </div>
            )}

            {/* Overlay with consent notice */}
            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center p-6 text-white text-center transition-all duration-300 group-hover:bg-black/30">
              <div className="bg-[var(--color-cta)] backdrop-blur-sm rounded-full p-4 mb-4 transition-transform duration-300 group-hover:scale-110">
                <Play className="w-8 h-8 fill-white" />
              </div>

              <h3 className="text-lg font-semibold mb-2">{title}</h3>

              {!consentGiven ? (
                <>
                  <p className="text-xs text-white/80 mb-3 max-w-sm">
                    Mit Klick auf „Video ansehen" öffnen Sie YouTube in einem neuen Tab.
                    Es gelten die <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Datenschutzbestimmungen von Google</a>.
                  </p>
                  <Button
                    className="bg-[var(--color-cta)] hover:bg-[var(--color-cta-hover)] text-white transition-all duration-300 gap-2"
                    onClick={() => {
                      setConsentGiven(true);
                      window.open(youtubeUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <Play className="w-4 h-4" />
                    Video auf YouTube ansehen
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Button
                  asChild
                  className="bg-[var(--color-cta)] hover:bg-[var(--color-cta-hover)] text-white transition-all duration-300 gap-2"
                >
                  <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">
                    <Play className="w-4 h-4" />
                    Auf YouTube ansehen
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              )}

              <p className="text-xs text-white/70 mt-3">
                Öffnet YouTube in neuem Tab
              </p>
            </div>
          </AspectRatio>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-2">
            Mit Klick öffnen Sie YouTube. Es gelten Googles <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline text-primary">Datenschutzbestimmungen</a>.
          </p>
          <Button
            size="lg"
            className="bg-[var(--color-cta)] hover:bg-[var(--color-cta-hover)] text-white gap-2"
            onClick={() => window.open(youtubeUrl, '_blank', 'noopener,noreferrer')}
          >
            <Play className="w-5 h-5" />
            {title}
            <ExternalLink className="w-4 h-4" />
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Öffnet YouTube in neuem Tab
          </p>
        </div>
      )}

      {description && (
        <p className="text-sm text-joerg-600 mt-3">{description}</p>
      )}
    </div>
  );
};

export default YouTubeLink;
